import { PayloadAction } from '@reduxjs/toolkit';
import { ConfigSelectors, StatsActions } from '@web_opxp/features';
import { AxiosResponse } from 'axios';
import { all, call, put, select, take, takeEvery, takeLatest, takeLeading } from 'redux-saga/effects';
import { ApiService } from 'web_core_library';
import { PRIVACY_HIGHSCORE } from '../base/constants';
import { ChallengeSelectors } from '../challenge';
import HighscoresService from './highscoresService';
import * as Selectors from './selectors';
import { HighscoresActions } from './slice';
import { HighscoreFilter, HighscoresError, IHighscoresApiResponse } from './types';

export function* initFeatureSaga() {
  yield call(HighscoresService.init, ApiService);
}

export function* loadExerciseHighscoresSaga() {
  yield put(HighscoresActions.loadHighscoresPage(1));
}

export function* loadHighscoresPageSaga(action: PayloadAction<number>) {
  const page = action.payload;
  try {
    const apiPage = page - 1;
    const exerciseId = yield select(Selectors.getExerciseId);
    if (!exerciseId) {
      // user has switched exercise
      yield put(HighscoresActions.updateHighscores(null, 0, []));
      return;
    }
    const isChallenge: boolean = yield select(Selectors.getChallengeFlag);
    const currentFilter: HighscoreFilter = yield select(Selectors.getFilter);
    const filter = currentFilter === 'friends';
    const response: AxiosResponse<IHighscoresApiResponse> = yield call(
      HighscoresService.getExerciseHighscores,
      exerciseId,
      apiPage,
      filter,
      isChallenge
    );
    const { currentUser, totalUsers, userList } = response.data;
    yield put(HighscoresActions.updateHighscores(currentUser, totalUsers, userList));
  } catch (error: any) {
    yield put(HighscoresActions.updateHighscores(null, 0, []));
    throw new HighscoresError(error.message);
  }
}

export function* filterHighscoresSaga() {
  // start loading first page of highscores for new filter
  yield put(HighscoresActions.loadHighscoresPage(1));
}

export function* participateInHighscoresSaga() {
  // update stats
  yield put(StatsActions.updateBooleanStat(PRIVACY_HIGHSCORE, true));
  yield put(StatsActions.saveStatsQueue());
  const result = yield take([StatsActions.saveStatsQueueSuccess, StatsActions.saveStatsQueueFailed]);
  if (result.type !== StatsActions.saveStatsQueueSuccess.type) {
    // error will be shown as toaster anyway
    return;
  }
  // load highscores for current exercise
  const todaysChallengeWorkoutId: number | null = yield select(ChallengeSelectors.getTodaysChallengeWorkoutId);

  if (!todaysChallengeWorkoutId) {
    return;
  }
  const exerciseId = yield select(ConfigSelectors.getWorkoutExerciseId, todaysChallengeWorkoutId);
  yield put(HighscoresActions.loadHighscores(exerciseId, true));
}

export default function* featureWatcher() {
  yield all([
    takeEvery(HighscoresActions.initHighscores, initFeatureSaga),
    takeLatest(HighscoresActions.loadHighscores, loadExerciseHighscoresSaga),
    takeLatest(HighscoresActions.loadHighscoresPage, loadHighscoresPageSaga),
    takeEvery(HighscoresActions.filterHighscores, filterHighscoresSaga),
    takeLeading(HighscoresActions.confirmParticipation, participateInHighscoresSaga),
  ]);
}
